import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import PageHeader from "@/components/PageHeader";

const CURRENT_VERSION = "1.1";
const LAST_UPDATED = "26. Februar 2026";

function Layout({ title, children }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };
  return (
    <div className="min-h-screen bg-background">
      <header className="max-w-4xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <button
          type="button"
          onClick={goBack}
          className="text-sm text-muted-foreground hover:text-foreground"
          data-testid="legal-back"
        >
          ← Zurück
        </button>
        <Link to="/" className="inline-flex">
          <Logo variant="name" height={40} testid="legal-logo" />
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-8 pb-24">
        <h1 className="font-heading text-3xl tracking-tight mb-2">{title}</h1>
        <p className="text-xs text-muted-foreground mb-8">
          Version {CURRENT_VERSION} · Stand: {LAST_UPDATED}
        </p>
        <div className="prose prose-invert max-w-none text-foreground/90 space-y-4 text-sm leading-relaxed">
          {children}
        </div>
      </main>
    </div>
  );
}

/** In-App-Wrapper: rendert Legal-Content innerhalb der AppShell (Menü + Logo
 *  bleiben durch AppShell sichtbar). Kein eigener Header, kein Zurück-Button. */
function InAppLegal({ title, eyebrow = "Rechtliches", children }) {
  return (
    <div data-testid="legal-inapp">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={`Version ${CURRENT_VERSION} · Stand: ${LAST_UPDATED}`}
        testid="legal-inapp-header"
      />
      <div className="prose prose-invert max-w-none text-foreground/90 space-y-4 text-sm leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function H2({ children }) {
  return <h2 className="font-heading text-lg mt-8 mb-3 text-foreground">{children}</h2>;
}

function H3({ children }) {
  return <h3 className="font-heading text-base mt-5 mb-2 text-foreground">{children}</h3>;
}

export function Impressum() {
  return (
    <Layout title="Impressum">
      <ImpressumBody />
    </Layout>
  );
}

export function ImpressumApp() {
  return (
    <InAppLegal title="Impressum">
      <ImpressumBody />
    </InAppLegal>
  );
}

function ImpressumBody() {
  return (
    <>
      <H2>Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz)</H2>
      <p>
        Felix Greve<br />
        TradeMind (Einzelunternehmer)<br />
        Freiburger Straße 79<br />
        79853 Lenzkirch-Saig<br />
        Deutschland
      </p>

      <H2>Kontakt</H2>
      <p>
        E-Mail: <a href="mailto:info@trading-setup.de" className="underline">info@trading-setup.de</a>
      </p>

      <H2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</H2>
      <p>Felix Greve (Anschrift wie oben)</p>

      <H2>Umsatzsteuer</H2>
      <p>
        Umsatzsteuerbefreit gemäß § 19 UStG (Kleinunternehmerregelung), es sei denn, es liegt eine
        Umsatzsteuer-Identifikationsnummer vor.
      </p>

      <H2>Streitschlichtung</H2>
      <p>
        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="underline">
          https://ec.europa.eu/consumers/odr
        </a>. Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>

      <H2>Haftungsausschluss</H2>
      <p>
        TradeMind ist ein Werkzeug zur Selbstreflexion und Dokumentation von Trading-Aktivitäten.
        Es stellt <strong>keine Anlage-, Finanz- oder Steuerberatung</strong> dar. Für Inhalte
        externer Broker-APIs und verlinkter Seiten übernehmen wir keine Haftung.
      </p>
    </>
  );
}

export function Datenschutz() {
  return (
    <Layout title="Datenschutzerklärung">
      <DatenschutzBody />
    </Layout>
  );
}

export function DatenschutzApp() {
  return (
    <InAppLegal title="Datenschutzerklärung">
      <DatenschutzBody />
    </InAppLegal>
  );
}

function DatenschutzBody() {
  return (
    <>
      <H2>1. Verantwortlicher</H2>
      <p>
        Verantwortlich für die Datenverarbeitung auf dieser Website ist:<br />
        <strong>Felix Greve</strong>, Freiburger Straße 79, 79853 Lenzkirch-Saig, Deutschland<br />
        E-Mail: <a href="mailto:info@trading-setup.de" className="underline">info@trading-setup.de</a>
      </p>
      <p>
        Ein Datenschutzbeauftragter ist gemäß § 38 BDSG nicht erforderlich, da wir weder in der Regel
        mehr als 20 Personen mit der automatisierten Verarbeitung personenbezogener Daten
        beschäftigen noch eine Kerntätigkeit im Sinne von Art. 37 DSGVO ausüben.
      </p>

      <H2>2. Grundsätze der Verarbeitung</H2>
      <p>
        Wir verarbeiten personenbezogene Daten nur, soweit es zur Bereitstellung der Plattform,
        zur Erfüllung vertraglicher Pflichten oder aufgrund gesetzlicher Vorgaben erforderlich
        ist. Rechtsgrundlagen sind je nach Verarbeitung:
      </p>
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Art. 6 Abs. 1 lit. b DSGVO</strong> — Vertragserfüllung (Konto, Journal, Broker-Sync)</li>
        <li><strong>Art. 6 Abs. 1 lit. c DSGVO</strong> — gesetzliche Pflichten (§ 147 AO, Steuerdaten)</li>
        <li><strong>Art. 6 Abs. 1 lit. f DSGVO</strong> — berechtigtes Interesse (IT-Sicherheit, Logs)</li>
        <li><strong>Art. 6 Abs. 1 lit. a DSGVO</strong> — Einwilligung (z. B. optionale Broker-Anbindung)</li>
      </ul>

      <H2>3. Welche Daten wir verarbeiten</H2>

      <H3>3.1 Kontodaten</H3>
      <p>
        Bei der Registrierung erheben wir: <strong>E-Mail-Adresse, Name, verschlüsseltes Passwort
        (bcrypt-Hash)</strong>, deinen selbstgewählten Erfahrungsstand (Orientierung / Struktur /
        Optimierung) und optional ein Ziel. Zusätzlich speichern wir Zeitpunkt der Registrierung,
        Zeitpunkt des letzten Logins sowie die Version der zugestimmten Datenschutzerklärung und
        Nutzungsbedingungen (Consent-Log, revisionssicher).
      </p>

      <H3>3.2 Rechnungsadresse und Zahlungsdaten</H3>
      <p>
        Wenn du ein kostenpflichtiges Abo abschließt, erheben wir zusätzlich <strong>Vorname,
        Nachname, Straße und Hausnummer, PLZ, Ort und Land</strong> als Rechnungsadresse.
        Diese Daten leiten wir an unseren Zahlungsdienstleister <strong>Stripe Payments Europe,
        Ltd. (Irland)</strong> weiter, der sie zur Rechnungserstellung, Zahlungsabwicklung und
        Buchhaltung verarbeitet. Als Zahlungsmethoden bietet Stripe im Checkout <strong>Kredit-
        und Debitkarte, SEPA-Lastschrift, PayPal und Stripe Link</strong> an. Zahlungs- und
        Kartendaten werden ausschließlich bei Stripe verarbeitet und berühren unsere Server
        nicht. Rechtsgrundlagen: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) und
        Art. 6 Abs. 1 lit. c DSGVO (§ 14 UStG, Rechnungspflichtangaben).
      </p>

      <H3>3.3 Trading-Journal</H3>
      <p>
        Alle Daten, die du selbst erfasst: Trade-Einträge (Asset, Ein- und Ausstieg, Größe, Gebühren,
        Datum), Strategien, Reflexionen, Stimmungen, Notizen und Chart-Screenshots (max. 3 MB pro
        Datei, gespeichert innerhalb deines Konto-Dokuments).
      </p>

      <H3>3.4 Broker-API-Zugangsdaten</H3>
      <p>
        Wenn du einen Broker (OKX, Bitget, Binance, Kraken, Bitunix) anbindest, speichern wir deine
        API-Credentials <strong>symmetrisch verschlüsselt (Fernet / AES-128-CBC + HMAC-SHA256)</strong>.
        Der Verschlüsselungs-Key liegt ausschließlich auf dem Server und wird nicht mit der
        Datenbank zusammen gespeichert. Wir empfehlen dringend, ausschließlich <strong>Read-Only-Keys
        ohne Auszahlungsberechtigung</strong> zu erzeugen.
      </p>

      <H3>3.5 KI-Coach-Konversationen</H3>
      <p>
        Coach-Chats im freien Chat werden <strong>nicht persistent gespeichert</strong> — sie
        existieren nur während deiner Session im Browser. Coach-Nachrichten, die einer Strategie
        zugeordnet sind, speichern wir zusammen mit der Strategie, damit du sie später
        nachschlagen kannst.
      </p>

      <H3>3.6 Support-Tickets</H3>
      <p>
        Wenn du ein Support-Ticket öffnest, speichern wir Betreff, Nachricht, ggf. den bisherigen
        Chatverlauf mit dem KI-Support, deinen Namen und deine E-Mail-Adresse. Die Ticket-Mail geht
        an <a href="mailto:info@trading-setup.de" className="underline">info@trading-setup.de</a>.
      </p>

      <H3>3.7 Server- und Session-Cookies</H3>
      <p>
        Wir setzen ausschließlich <strong>technisch notwendige Cookies</strong> (§ 25 Abs. 2 Nr. 2 TDDDG):
      </p>
      <ul className="list-disc ml-5 space-y-1">
        <li><code>access_token</code> — httpOnly, Secure, SameSite=Lax, Laufzeit 15 Min.</li>
        <li><code>refresh_token</code> — httpOnly, Secure, SameSite=Lax, Laufzeit 7 Tage</li>
      </ul>
      <p>
        Diese Cookies sind für Login und Sitzungsverwaltung zwingend erforderlich; eine
        Einwilligung ist nicht notwendig. Wir nutzen <strong>keine Tracking-Cookies, kein Google
        Analytics, kein Meta Pixel und keine Werbe-Netzwerke</strong>. Im lokalen Browser-Speicher
        (localStorage) legen wir nur UX-Zustand ab (z. B. Tour-Fortschritt).
      </p>

      <H3>3.8 Server-Logs</H3>
      <p>
        Aus Sicherheitsgründen protokolliert der Server IP-Adresse, Zeitpunkt, User-Agent und
        HTTP-Endpunkt zu jedem Zugriff. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (IT-Sicherheit,
        Missbrauchsabwehr). Löschung: spätestens nach 30 Tagen, sofern kein Sicherheitsvorfall
        vorliegt.
      </p>

      <H2>4. Auftragsverarbeiter und Drittanbieter</H2>

      <H3>4.1 Emergent (Hosting + KI-Plattform)</H3>
      <p>
        Die App wird auf der Infrastruktur der <strong>Emergent Labs</strong> gehostet
        (Kubernetes-Cluster inkl. MongoDB). Emergent verarbeitet Daten in unserem Auftrag als
        Auftragsverarbeiter nach Art. 28 DSGVO. Ein Auftragsverarbeitungsvertrag mit Emergent Labs
        wird angefragt und auf Anforderung nach Abschluss offengelegt. Serverstandort und
        Standardvertragsklauseln (falls Drittland-Transfer erfolgt) werden ebenfalls dokumentiert
        und stehen auf schriftliche Anfrage zur Einsicht bereit.
      </p>

      <H3>4.2 Anthropic (KI-Coach über Emergent-LLM-Layer)</H3>
      <p>
        Der KI-Coach basiert auf <strong>Claude Sonnet 4.6 (Anthropic PBC, USA)</strong>. Deine
        Coach-Anfragen, Chart-Screenshots und ausgewählte Kontextauszüge (Trefferquote, Netto-P/L,
        Notizen) werden über den Emergent-LLM-Layer zur Antwortgenerierung an Anthropic übermittelt.
        Es handelt sich um eine Verarbeitung in einem <strong>Drittland (USA)</strong>. Rechtsgrundlagen:
      </p>
      <ul className="list-disc ml-5 space-y-1">
        <li>Art. 46 Abs. 2 lit. c DSGVO (Standardvertragsklauseln über den Emergent-AV) sowie ggf.</li>
        <li>Art. 45 DSGVO in Verbindung mit dem EU-US Data Privacy Framework (Anthropic-Zertifizierung).</li>
      </ul>
      <p>
        Anthropic hat sich vertraglich verpflichtet, deine Eingaben <strong>nicht zum Training
        eigener Modelle</strong> zu verwenden. Bitte gib in Chat-Nachrichten und Screenshots keine
        besonderen Kategorien personenbezogener Daten (Art. 9 DSGVO), Klarnamen Dritter oder
        Zahlungsdaten preis.
      </p>

      <H3>4.3 Strato (E-Mail-Versand)</H3>
      <p>
        System-Mails (E-Mail-Bestätigung, Passwort-Reset, Support-Nachrichten, Ticket-Bestätigungen)
        versenden wir über den SMTP-Dienst der <strong>Strato AG, Otto-Ostrowski-Straße 7, 10249
        Berlin, Deutschland</strong>. Serverstandort: Deutschland. Ein Auftragsverarbeitungsvertrag
        besteht (Strato-Standard-AV).
      </p>

      <H3>4.4 Broker-APIs</H3>
      <p>
        Bei aktiver Broker-Verbindung senden wir Anfragen an OKX, Bitget, Binance, Kraken oder
        Bitunix. Diese Dienste sind eigenständige Verantwortliche im Sinne der DSGVO und haben
        eigene Datenschutzerklärungen. Wir übertragen ausschließlich die von dir gespeicherten
        API-Credentials — <strong>keine weiteren personenbezogenen Daten wie Name oder E-Mail</strong>.
      </p>

      <H2>5. Speicherdauer und Löschung</H2>
      <p>
        Wir speichern deine Daten so lange, wie es für die Vertragsdurchführung erforderlich ist,
        maximal aber bis zu deiner Kontolöschung. Ausnahmen aus gesetzlichen Aufbewahrungspflichten:
      </p>
      <ul className="list-disc ml-5 space-y-1">
        <li>
          <strong>Steuerrelevante Handelsdaten</strong> unterliegen der 10-jährigen
          Aufbewahrungspflicht nach § 147 Abs. 3 AO. Nach Kontolöschung werden diese Datensätze
          <strong> anonymisiert</strong> weitergeführt (keine Zuordnung zu deiner Person mehr möglich),
          bis die Aufbewahrungsfrist abgelaufen ist. Anschließend werden auch die anonymisierten
          Datensätze gelöscht.
        </li>
        <li>
          <strong>Consent-Logs</strong> (Einwilligungen zu Datenschutz/AGB) werden für die Dauer
          von 3 Jahren nach Kontolöschung als Nachweis aufbewahrt (Art. 7 Abs. 1 DSGVO).
        </li>
        <li>
          <strong>Support-Tickets</strong> werden 12 Monate nach Abschluss gelöscht.
        </li>
      </ul>

      <H2>6. Deine Rechte nach der DSGVO</H2>
      <p>Du hast jederzeit das Recht auf:</p>
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Auskunft</strong> über die zu deiner Person gespeicherten Daten (Art. 15)</li>
        <li><strong>Berichtigung</strong> unrichtiger Daten (Art. 16)</li>
        <li><strong>Löschung</strong> deiner Daten (Art. 17), soweit nicht gesetzlich anders vorgeschrieben</li>
        <li><strong>Einschränkung</strong> der Verarbeitung (Art. 18)</li>
        <li><strong>Datenübertragbarkeit</strong> in einem maschinenlesbaren Format (Art. 20)</li>
        <li><strong>Widerspruch</strong> gegen Verarbeitungen auf Basis berechtigter Interessen (Art. 21)</li>
        <li><strong>Widerruf</strong> erteilter Einwilligungen mit Wirkung für die Zukunft (Art. 7 Abs. 3)</li>
      </ul>
      <p>
        Zur Ausübung deiner Rechte reicht eine formlose E-Mail an{" "}
        <a href="mailto:info@trading-setup.de" className="underline">info@trading-setup.de</a>. Wir
        antworten innerhalb der gesetzlichen Frist von einem Monat.
      </p>

      <H2>7. Beschwerderecht bei der Aufsichtsbehörde</H2>
      <p>
        Du hast das Recht, dich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung deiner
        personenbezogenen Daten zu beschweren (Art. 77 DSGVO). Zuständig für den Verantwortlichen ist:
      </p>
      <p>
        <strong>Der Landesbeauftragte für den Datenschutz und die Informationsfreiheit
        Baden-Württemberg</strong><br />
        Königstraße 10a, 70173 Stuttgart<br />
        Telefon: +49 711 615541-0<br />
        E-Mail: poststelle@lfdi.bwl.de<br />
        Web: <a href="https://www.baden-wuerttemberg.datenschutz.de" target="_blank" rel="noopener noreferrer" className="underline">www.baden-wuerttemberg.datenschutz.de</a>
      </p>

      <H2>8. Datensicherheit</H2>
      <p>
        Wir treffen technische und organisatorische Maßnahmen entsprechend Art. 32 DSGVO:
        TLS-Verschlüsselung aller Verbindungen (HTTPS), Passwort-Hashing mit bcrypt,
        AES-Verschlüsselung der Broker-Credentials (Fernet), rollenbasierte Zugriffskontrolle,
        Backup-Konzept, regelmäßige Sicherheitsupdates.
      </p>

      <H2>9. Automatisierte Entscheidungsfindung</H2>
      <p>
        Der KI-Coach gibt Reflexionsimpulse und Analysen, trifft aber <strong>keine automatischen
        Handelsentscheidungen</strong> und führt keine Trades aus. Es findet keine automatisierte
        Entscheidungsfindung im Sinne von Art. 22 DSGVO statt.
      </p>

      <H2>10. Änderungen dieser Erklärung</H2>
      <p>
        Wir behalten uns vor, diese Datenschutzerklärung anzupassen, wenn sich rechtliche oder
        tatsächliche Rahmenbedingungen ändern. Änderungen werden auf dieser Seite mit einer neuen
        Versionsnummer veröffentlicht. Bei wesentlichen Änderungen holen wir deine erneute
        Zustimmung ein.
      </p>

      <p className="text-muted-foreground text-xs mt-8">
        Version {CURRENT_VERSION} · Stand {LAST_UPDATED}. Diese Erklärung beschreibt den aktuellen
        Datenverarbeitungsstand der TradeMind-Plattform. Für eine juristisch abschließende Prüfung
        empfehlen wir eine anwaltliche Kontrolle vor Skalierung des Angebots.
      </p>
    </>
  );
}

export function AGB() {
  return (
    <Layout title="Nutzungsbedingungen (AGB)">
      <AGBBody />
    </Layout>
  );
}

export function AGBApp() {
  return (
    <InAppLegal title="Nutzungsbedingungen (AGB)">
      <AGBBody />
    </InAppLegal>
  );
}

function AGBBody() {
  return (
    <>
      <H2>1. Geltungsbereich</H2>
      <p>
        Diese Nutzungsbedingungen regeln das Vertragsverhältnis zwischen dir („Nutzer") und Felix
        Greve, Freiburger Straße 79, 79853 Lenzkirch-Saig (nachfolgend „TradeMind") über die
        Nutzung der Web-Plattform TradeMind.
      </p>

      <H2>2. Vertragsgegenstand und Leistungsumfang</H2>
      <p>
        TradeMind stellt eine Plattform zur <strong>Selbstreflexion, Dokumentation und Analyse
        eigener Trading-Aktivitäten</strong> bereit. Kernfunktionen sind: Trading-Journal,
        Strategie-Verwaltung, Analyse-Auswertungen, KI-Coach (auf Basis Claude Sonnet 4.6),
        Steuer-Report-Generator, optionale Read-Only-Anbindung von Krypto-Brokern.
      </p>

      <H2>3. Kein Finanz- oder Steuerberatungsverhältnis</H2>
      <p>
        TradeMind erbringt <strong>keine Anlage-, Vermögens-, Finanz- oder Steuerberatung</strong> im
        Sinne von KWG, WpHG, StBerG oder verwandten Gesetzen. Alle Analysen, Hinweise, KI-Ausgaben,
        Steuer-Berichte und Reflexionsimpulse dienen ausschließlich der eigenverantwortlichen
        Reflexion und ersetzen keine Beratung durch qualifizierte Fachleute. Vor
        steuer­relevanten Erklärungen empfehlen wir die Rücksprache mit einer Steuerberaterin
        oder einem Steuerberater.
      </p>

      <H2>4. Registrierung und Konto</H2>
      <p>
        Die Nutzung setzt ein Konto voraus. Du versicherst, das 18. Lebensjahr vollendet zu haben
        und die eingegebenen Daten wahrheitsgemäß angegeben zu haben. Für Handlungen unter deinem
        Konto bist du selbst verantwortlich; halte deine Zugangsdaten geheim.
      </p>

      <H2>5. Preise und Testphase</H2>
      <p>
        Neue Nutzerinnen und Nutzer erhalten nach der Registrierung eine <strong>30-tägige
        kostenlose Testphase der Vollversion</strong>. Danach wechselt das Konto automatisch in die
        <strong> kostenlose Standardversion</strong>, sofern kein Abonnement abgeschlossen wurde.
      </p>

      <H3>5.1 Standardversion (kostenlos)</H3>
      <ul className="list-disc ml-5 space-y-1">
        <li>Trading-Journal mit Basis-Analyse</li>
        <li>Maximal 2 gespeicherte Strategien</li>
        <li>Maximal 50 Trade-Einträge pro Kalenderjahr</li>
        <li>Kein Steuer-Tool</li>
        <li>Eingeschränkte Nutzung des KI-Coaches</li>
      </ul>

      <H3>5.2 Vollversion (kostenpflichtig)</H3>
      <p>
        <strong>8,99 € pro Monat</strong> oder <strong>84,99 € pro Jahr</strong> (jeweils inklusive
        gesetzlicher Umsatzsteuer, soweit anwendbar). Enthält uneingeschränkte Nutzung aller
        Funktionen inklusive Steuer-Tool, unbegrenzter Trades und Strategien sowie vollständigem
        KI-Coach.
      </p>

      <H3>5.3 Zahlungsabwicklung, Zahlungsmethoden, Rechnungen und Steuer</H3>
      <p>
        Alle Zahlungen werden über den Zahlungsdienstleister <strong>Stripe Payments Europe,
        Ltd.</strong> abgewickelt. Als Zahlungsmethoden stehen dir im Checkout zur Verfügung:
        <strong> Kredit- und Debitkarte (Visa, Mastercard, American Express), SEPA-Lastschrift
        (Basis-Mandat), PayPal</strong> und <strong>Stripe Link</strong> (gespeicherte
        Zahlungsdaten). Nach jeder erfolgreichen Zahlung erhältst du automatisch eine Rechnung
        per E-Mail mit direktem PDF-Download. Die Rechnung enthält:
      </p>
      <ul className="list-disc ml-5 space-y-1">
        <li>Eine fortlaufende, eindeutige <strong>Rechnungsnummer</strong> (durch Stripe vergeben)</li>
        <li>Deine <strong>Kundennummer</strong> (Format K-JJJJ-XXXXXX, im Konto einsehbar)</li>
        <li>Rechnungsdatum und Abrechnungszeitraum</li>
        <li>Deine hinterlegte Rechnungsadresse</li>
        <li>Anbieter-Angaben nach § 14 UStG (Name, Anschrift, Kontakt)</li>
        <li>Nettobetrag (siehe Umsatzsteuer-Hinweis unten)</li>
      </ul>
      <p>
        <strong>Umsatzsteuer:</strong> TradeMind wird von Felix Greve als Kleinunternehmer
        gemäß <strong>§ 19 UStG</strong> betrieben. Deshalb wird <strong>keine Umsatzsteuer
        ausgewiesen</strong>, und du kannst keine Vorsteuer aus TradeMind-Rechnungen abziehen.
        Der auf der Rechnung ausgewiesene Betrag ist zugleich der Bruttobetrag.
      </p>

      <H2>6. Widerrufsrecht für Verbraucher</H2>
      <p>
        Als Verbraucher hast du das Recht, binnen 14 Tagen ohne Angabe von Gründen einen
        kostenpflichtigen Vertrag zu widerrufen. Die Widerrufsfrist beträgt 14 Tage ab dem Tag des
        Vertragsabschlusses.
      </p>
      <p>
        Um dein Widerrufsrecht auszuüben, sende eine eindeutige Erklärung (z. B. formlose E-Mail)
        an <a href="mailto:info@trading-setup.de" className="underline">info@trading-setup.de</a>.
        Die Rückzahlung erfolgt binnen 14 Tagen über dasselbe Zahlungsmittel, das du bei der
        ursprünglichen Transaktion verwendet hast.
      </p>
      <p>
        <strong>Hinweis zum vorzeitigen Erlöschen:</strong> Das Widerrufsrecht erlischt, wenn wir
        mit der vollständigen Ausführung der Dienstleistung begonnen haben, nachdem du dazu deine
        ausdrückliche Zustimmung gegeben und gleichzeitig deine Kenntnis davon bestätigt hast, dass
        du dein Widerrufsrecht bei vollständiger Vertragserfüllung verlierst.
      </p>

      <H2>7. Kündigung</H2>
      <p>
        Ein monatliches Abonnement kann jederzeit zum Ende der laufenden Abrechnungsperiode
        gekündigt werden. Ein Jahresabonnement kann zum Ende der Laufzeit gekündigt werden. Die
        Kündigung erfolgt über die Einstellungen im Konto oder per E-Mail an{" "}
        <a href="mailto:info@trading-setup.de" className="underline">info@trading-setup.de</a>.
      </p>
      <p>
        Nach Kündigung eines Abonnements wechselt dein Konto in die Standardversion; deine Daten
        bleiben erhalten. Du kannst dein Konto zusätzlich vollständig löschen (siehe Ziffer 8).
      </p>

      <H2>8. Kontolöschung und Datenaufbewahrung</H2>
      <p>
        Du kannst dein Konto jederzeit über die Einstellungen oder per E-Mail-Anfrage löschen
        lassen. Deine persönlichen Daten (Konto, Notizen, Reflexionen, Anhänge, Broker-Credentials)
        werden entfernt. Steuerrelevante Handelsdaten werden nach § 147 AO für 10 Jahre in
        <strong> anonymisierter</strong> Form aufbewahrt (keine Zuordnung zu deiner Person mehr
        möglich) und anschließend gelöscht. Details siehe Datenschutzerklärung, Ziffer 5.
      </p>

      <H2>9. Verantwortlichkeit des Nutzers</H2>
      <p>
        Alle Handelsentscheidungen triffst du eigenverantwortlich. TradeMind gibt keine
        Handelssignale und führt keine automatischen Trades aus. Broker-API-Anbindungen sind
        ausschließlich als <strong>Read-Only</strong> zu konfigurieren; die Verantwortung für die
        korrekte Konfiguration liegt beim Nutzer.
      </p>

      <H2>10. Haftung</H2>
      <p>
        TradeMind haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie für Schäden aus
        der Verletzung von Leben, Körper oder Gesundheit. Bei einfacher Fahrlässigkeit haftet
        TradeMind nur bei Verletzung wesentlicher Vertragspflichten und begrenzt auf den
        vertragstypisch vorhersehbaren Schaden.
      </p>
      <p>
        Für Handelsverluste, entgangenen Gewinn oder steuerliche Folgen aus der Nutzung der
        Plattform übernimmt TradeMind keine Haftung, soweit nicht Vorsatz oder grobe
        Fahrlässigkeit vorliegt.
      </p>

      <H2>11. Verfügbarkeit</H2>
      <p>
        Wir bemühen uns um eine hohe Verfügbarkeit, garantieren aber keine ununterbrochene
        Erreichbarkeit. Wartungsarbeiten und externe Störungen (Broker-APIs, Cloud-Anbieter) können
        zu vorübergehenden Einschränkungen führen.
      </p>

      <H2>12. Änderungen dieser Bedingungen</H2>
      <p>
        Wir behalten uns vor, diese Bedingungen anzupassen. Änderungen kündigen wir mindestens 30
        Tage im Voraus per E-Mail an. Widersprichst du nicht innerhalb dieser Frist, gelten die
        neuen Bedingungen als akzeptiert.
      </p>

      <H2>13. Schlussbestimmungen</H2>
      <p>
        Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Ist der Nutzer Kaufmann,
        juristische Person des öffentlichen Rechts oder öffentlich-rechtliches Sondervermögen, ist
        Gerichtsstand der Sitz des Anbieters. Sollten einzelne Bestimmungen unwirksam sein, bleibt
        die Wirksamkeit der übrigen Bedingungen unberührt.
      </p>

      <p className="text-muted-foreground text-xs mt-8">
        Version {CURRENT_VERSION} · Stand {LAST_UPDATED}. Für eine juristisch abschließende
        Prüfung empfehlen wir vor Aktivierung der kostenpflichtigen Funktionen eine anwaltliche
        Kontrolle.
      </p>
    </>
  );
}
